import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { listDevices, listAllChannels } from "../api/devices";
import { useDevicesStore } from "../store/devicesStore";

export function useDevices() {
  const setDevices = useDevicesStore((s) => s.setDevices);
  const setAllChannels = useDevicesStore((s) => s.setAllChannels);

  const q = useQuery({
    queryKey: ["devices"],
    queryFn: listDevices,
    refetchInterval: 15_000,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (!q.data) return;
    setDevices(q.data);
  }, [q.data, setDevices]);

  // Carga BULK de channels: una sola request al backend en lugar de N requests
  // paralelas (una por device). Antes con 355+ devices, el browser saturaba
  // el pool HTTP/2 y cortaba algunas conexiones con net::ERR_FAILED al azar.
  // El endpoint /channels devuelve toda la lista filtrada por accesibilidad
  // del usuario; aca la agrupamos por device_id y poblamos el store.
  // staleTime alto: los channels son estaticos hasta que admin haga sync;
  // refetch periodico (60s) capta cambios eventuales.
  const channelsQuery = useQuery({
    queryKey: ["channels-all"],
    queryFn: listAllChannels,
    refetchInterval: 60_000,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!channelsQuery.data) return;
    // Agrupar la lista plana por device_id. El backend ya viene ordenado
    // por device_id, channel_number, asi que cada lista queda ordenada.
    const grouped = new Map<number, typeof channelsQuery.data>();
    for (const ch of channelsQuery.data) {
      const list = grouped.get(ch.device_id) ?? [];
      list.push(ch);
      grouped.set(ch.device_id, list);
    }
    // Un solo set (una sola escritura de localStorage) para todos los
    // equipos; con la lista vacia no se escribe nada, como antes.
    if (grouped.size === 0) return;
    setAllChannels(Object.fromEntries(grouped));
  }, [channelsQuery.data, setAllChannels]);

  return q;
}
